import { WindowEvent } from 'library/ts/ui/events/WindowEvent';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Global handler for suppressing avia internal promise rejections
 *
 * This utility addresses race conditions in the avia-js library that occur during rapid
 * tag creation/destruction cycles. When tags are created and destroyed in quick succession,
 * avia's internal promise chains can complete after the player instance has been destroyed,
 * resulting in "Cannot read properties of undefined (reading 'registerProxy')" errors.
 *
 * Additionally handles seek parameter validation errors that occur when invalid parameters
 * (NaN, Infinity) are passed to the avia seek method, preventing console noise while
 * maintaining proper error logging for debugging.
 *
 * These errors are purely cosmetic console noise and don't affect functionality, but they
 * can be alarming to developers. This handler intercepts and suppresses these specific
 * avia internal errors while preserving all other promise rejections.
 */

let aviaErrorHandlerRegistered = false;
let aviaErrorHandlerFunction: ( ( event: PromiseRejectionEvent ) => void ) | null = null;

/**
 * @description Registers a global handler to suppress avia internal promise rejections
 * This prevents console noise from avia library's internal race conditions during rapid operations
 * and seek parameter validation errors that don't affect functionality.
 *
 * The handler specifically targets:
 * - Errors from avia.cjs (avia library internals)
 * - registerProxy errors (avia's internal hook registration system)
 * - seek(position) parameter validation errors with NaN/invalid values
 *
 * All other unhandled promise rejections pass through normally for proper debugging.
 *
 * @example
 * // Automatically called when this module is imported
 * import 'tag/ts/tag/player/adapters/avia/utilities/AviaErrorHandler';
 *
 * // Or manually if needed
 * registerAviaErrorHandler();
 */
export function registerAviaErrorHandler(): void {
	if ( !aviaErrorHandlerRegistered ) {
		// Store the handler function reference for later cleanup
		aviaErrorHandlerFunction = ( event ) => {
			// Check for avia-specific internal errors
			const isAviaInternalError = event.reason?.stack?.includes( 'avia.cjs' ) ||
				event.reason?.stack?.includes( 'registerProxy' );

			// Check for seek parameter validation errors
			const isSeekValidationError = event.reason?.message?.includes?.( 'seek(position)' ) &&
				event.reason?.message?.includes?.( 'API method called with missing or invalid param' );

			// Suppress both types of errors
			if ( isAviaInternalError || isSeekValidationError ) {
				// Log different messages for different error types
				if ( isAviaInternalError ) {
					console.warn( 'Suppressed avia internal promise rejection:', event.reason );
				} else if ( isSeekValidationError ) {
					console.warn( 'Suppressed avia seek parameter validation error:', {
						message: event.reason?.message,
						error: event.reason,
						note: 'This error is handled by enhanced parameter validation and does not affect functionality'
					} );
				}
				event.preventDefault(); // Suppress the unhandled rejection error
			}
			// All other promise rejections pass through normally
		};

		window.addEventListener( WindowEvent.UNHANDLED_REJECTION, aviaErrorHandlerFunction );
		aviaErrorHandlerRegistered = true;
	}
}

/**
 * @description Unregisters the global avia error handler to prevent memory leaks
 * This should be called when Smart Tag is being destroyed or reset to ensure
 * proper cleanup of global event listeners and prevent memory accumulation.
 *
 * @example
 * // Call during Smart Tag cleanup/destruction
 * unregisterAviaErrorHandler();
 */
export function unregisterAviaErrorHandler(): void {
	if ( aviaErrorHandlerRegistered && aviaErrorHandlerFunction ) {
		window.removeEventListener( WindowEvent.UNHANDLED_REJECTION, aviaErrorHandlerFunction );
		aviaErrorHandlerFunction = null;
		aviaErrorHandlerRegistered = false;
	}
}

/**
 * @description Returns whether the avia error handler is currently registered
 * Useful for debugging and testing purposes
 *
 * @returns {boolean} True if handler is registered, false otherwise
 */
export function isAviaErrorHandlerRegistered(): boolean {
	return aviaErrorHandlerRegistered;
}

// Auto-register when module is imported
// This ensures the handler is active as soon as any avia component loads
registerAviaErrorHandler();